/**
 * Optimized Third-Party Loader with 100% Render Completion Detection
 * Ensures Google Analytics & AdSense load ONLY after complete render
 * MOBILE OPTIMIZED: Works with simplified mobile render detection
 */

import { getRenderCompleteDetector, onRenderComplete } from './renderCompleteDetector';

interface ThirdPartyScript {
  name: string;
  url: string;
  priority: number; // 1 = highest, 10 = lowest
  config?: any;
  onLoad?: () => void;
  onError?: (error: Error) => void;
}

interface LoadingConfig {
  enabled: boolean;
  delayAfterRender: number; // Additional delay after render completion
  maxConcurrent: number; // Maximum scripts loading simultaneously
  retryAttempts: number;
  retryDelay: number;
  // 🔧 NEW: Mobile optimization settings
  mobileDelayReduction: number; // Reduce delays on mobile for faster loading
}

// 🔧 NEW: Mobile detection utility (matches renderCompleteDetector)
const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
         window.innerWidth <= 768 ||
         'ontouchstart' in window;
};

export class OptimizedThirdPartyLoader {
  private scripts: Map<string, ThirdPartyScript> = new Map();
  private loadedScripts: Set<string> = new Set();
  private loadingScripts: Set<string> = new Set();
  private config: LoadingConfig;
  private initialized: boolean = false;
  private isMobile: boolean; // 🔧 NEW: Mobile detection flag
  private renderCompleteDetector: any = null; // 🔧 FIX: Use any type for now

  constructor(config?: Partial<LoadingConfig>) {
    this.isMobile = isMobileDevice();
    
    this.config = {
      enabled: true,
      delayAfterRender: this.isMobile ? 800 : 1200,
      maxConcurrent: 2,
      retryAttempts: 2,
      retryDelay: 500,
      mobileDelayReduction: 600,
      ...config
    };

    this.setupDefaultScripts();
    this.init();
  }

  private setupDefaultScripts(): void {
    // ✅ DISABLED: Google Analytics - Now loaded traditionally in HTML head
    // No longer loading Google Analytics through this loader

    // Google AdSense (kept for ads) - Only load on-demand
    this.registerScript({
      name: 'google-adsense',
      url: 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-8980858398695883',
      priority: 10, // Lowest priority - only load on-demand
      config: {
        client: 'ca-pub-8980858398695883',
        crossOrigin: 'anonymous',
        loadOnDemand: true // 🎯 Flag to skip auto-loading
      },
      onLoad: () => this.log('✅ [ThirdPartyLoader] Google AdSense loaded successfully'),
      onError: (error: Error) => this.log(`❌ [ThirdPartyLoader] Google AdSense failed to load: ${error.message}`)
    });
  }

  private init(): void {
    if (!this.config.enabled) {
      this.log(`🔧 [ThirdPartyLoader] Disabled in development mode (${this.isMobile ? 'Mobile' : 'Desktop'})`);
      return;
    }

    this.log(`🚀 [ThirdPartyLoader] Initializing optimized loading for ${this.isMobile ? 'MOBILE' : 'DESKTOP'}...`);
    
    // Wait for complete render before starting
    onRenderComplete(() => {
      this.log(`✅ [ThirdPartyLoader] Render complete detected on ${this.isMobile ? 'mobile' : 'desktop'}, starting script loading...`);
      
      // 🔧 MOBILE OPTIMIZATION: Reduce delay for faster mobile loading
      const finalDelay = this.isMobile ? 
        Math.max(100, this.config.delayAfterRender - this.config.mobileDelayReduction) : 
        this.config.delayAfterRender;
      
      this.log(`⏱️ [ThirdPartyLoader] Using ${finalDelay}ms delay after render completion`);
      
      setTimeout(() => {
        this.startLoadingScripts();
      }, finalDelay);
    });
  }

  private async startLoadingScripts(): Promise<void> {
    if (!this.renderCompleteDetector) {
      this.log('⚠️ [ThirdPartyLoader] Render complete detector not ready');
      return;
    }

    this.log('🚀 [ThirdPartyLoader] Starting script loading process...');
    
    // Wait for render completion
    await this.renderCompleteDetector.waitForRenderComplete();
    
    // Add device-specific delay reduction for mobile
    const additionalDelay = this.isMobile 
      ? Math.max(0, this.config.delayAfterRender - this.config.mobileDelayReduction)
      : this.config.delayAfterRender;
    
    if (additionalDelay > 0) {
      this.log(`⏱️ [ThirdPartyLoader] Additional delay: ${additionalDelay}ms (${this.isMobile ? 'mobile optimized' : 'desktop'})`);
      await new Promise(resolve => setTimeout(resolve, additionalDelay));
    }

    // Get scripts sorted by priority, ✅ EXCLUDE AdSense from auto-loading
    const scriptsToLoad = Array.from(this.scripts.values())
      .filter(script => !script.config?.loadOnDemand) // 🎯 SKIP AdSense (has loadOnDemand: true)
      .sort((a, b) => a.priority - b.priority);

    this.log(`📝 [ThirdPartyLoader] Loading ${scriptsToLoad.length} scripts (excluding on-demand scripts like AdSense)...`);

    // Load scripts with concurrency control
    const concurrentLoads: Promise<void>[] = [];
    
    for (const script of scriptsToLoad) {
      if (this.loadedScripts.has(script.name) || this.loadingScripts.has(script.name)) {
        continue;
      }

      if (concurrentLoads.length >= this.config.maxConcurrent) {
        await Promise.race(concurrentLoads);
      }

      const loadPromise = this.loadScript(script).catch(error => {
        this.log(`❌ [ThirdPartyLoader] Failed to load ${script.name}:`, error);
      });

      concurrentLoads.push(loadPromise);
    }

    // Wait for all remaining loads to complete
    await Promise.allSettled(concurrentLoads);
    
    this.log('✅ [ThirdPartyLoader] Essential script loading completed (AdSense excluded - loads on-demand only)');
  }

  private async loadScript(script: ThirdPartyScript): Promise<void> {
    if (this.loadedScripts.has(script.name) || this.loadingScripts.has(script.name)) {
      return;
    }

    this.loadingScripts.add(script.name);
    this.log(`📥 [ThirdPartyLoader] Loading ${script.name}...`);

    try {
      await this.loadScriptWithRetry(script);
      this.loadedScripts.add(script.name);
      this.log(`✅ [ThirdPartyLoader] ${script.name} loaded successfully`);
      
      if (script.onLoad) {
        script.onLoad();
      }
    } catch (error) {
      this.log(`❌ [ThirdPartyLoader] ${script.name} failed to load:`, error);
      
      if (script.onError) {
        script.onError(error as Error);
      }
    } finally {
      this.loadingScripts.delete(script.name);
    }
  }

  private async loadScriptWithRetry(script: ThirdPartyScript): Promise<void> {
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= this.config.retryAttempts; attempt++) {
      try {
        await this.createAndLoadScript(script);
        return; // Success
      } catch (error) {
        lastError = error as Error;
        this.log(`⚠️ [ThirdPartyLoader] ${script.name} attempt ${attempt}/${this.config.retryAttempts} failed`);
        
        if (attempt < this.config.retryAttempts) {
          await new Promise(resolve => setTimeout(resolve, this.config.retryDelay * attempt));
        }
      }
    }

    throw lastError;
  }

  private createAndLoadScript(script: ThirdPartyScript): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if script already exists
      const existingScript = document.querySelector(`script[src="${script.url}"]`);
      if (existingScript) {
        resolve();
        return;
      }

      const scriptElement = document.createElement('script');
      scriptElement.src = script.url;
      scriptElement.async = true;

      // Apply script-specific configurations
      if (script.config?.crossOrigin) {
        scriptElement.crossOrigin = script.config.crossOrigin;
      }

      // Add loading attribute for better performance
      scriptElement.setAttribute('data-script-name', script.name);
      scriptElement.setAttribute('data-priority', script.priority.toString());

      scriptElement.onload = () => {
        this.log(`✅ [ThirdPartyLoader] Script element loaded: ${script.name}`);
        resolve();
      };

      scriptElement.onerror = () => {
        const error = new Error(`Failed to load script: ${script.url}`);
        this.log(`❌ [ThirdPartyLoader] Script element error: ${script.name}`);
        reject(error);
      };

      // Use requestIdleCallback for non-blocking insertion
      if (window.requestIdleCallback) {
        window.requestIdleCallback(() => {
          document.head.appendChild(scriptElement);
        }, { timeout: 1000 });
      } else {
        document.head.appendChild(scriptElement);
      }
    });
  }

  private initializeGoogleAnalytics(): void {
    this.log('📊 [ThirdPartyLoader] Initializing Google Analytics...');
    
    try {
      // Initialize dataLayer
      (window as any).dataLayer = (window as any).dataLayer || [];
      function gtag(...args: any[]) {
        (window as any).dataLayer.push(args);
      }
      (window as any).gtag = gtag;

      // ✅ FIX: Enhanced configuration with better tracking
      gtag('js', new Date());
      gtag('config', 'G-6QLQYFHSYV', {
        anonymize_ip: true,
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
        cookie_flags: 'secure;samesite=strict',
        send_page_view: true,
        // ✅ FIX: Add debug and development tracking
        debug_mode: process.env.NODE_ENV !== 'production',
        // ✅ FIX: Enhanced measurement settings
        enhanced_measurement: {
          scrolls: true,
          outbound_clicks: true,
          site_search: true,
          video_engagement: true,
          file_downloads: true
        }
      });

      // ✅ FIX: Send immediate page view with detailed info
      gtag('event', 'page_view', {
        page_title: document.title,
        page_location: window.location.href,
        page_path: window.location.pathname,
        page_referrer: document.referrer,
        // Custom dimensions
        site_section: 'main',
        content_type: 'page',
        user_engagement: 'active'
      });

      // ✅ FIX: Track third party loader initialization
      gtag('event', 'third_party_loaded', {
        event_category: 'technical',
        event_label: 'google_analytics',
        value: 1,
        device_type: this.isMobile ? 'mobile' : 'desktop'
      });

      this.log('✅ [ThirdPartyLoader] Google Analytics initialized with enhanced tracking');
    } catch (error) {
      this.log('❌ [ThirdPartyLoader] Google Analytics initialization failed:', error);
    }
  }

  private initializeGoogleAdSense(): void {
    this.log('💰 [ThirdPartyLoader] Google AdSense script loaded');
    
    try {
      // AdSense is auto-initialized by the script
      // We just need to ensure adsbygoogle array exists
      if (!(window as any).adsbygoogle) {
        (window as any).adsbygoogle = [];
      }

      this.log('✅ [ThirdPartyLoader] Google AdSense ready for ads');
    } catch (error) {
      this.log('❌ [ThirdPartyLoader] Google AdSense initialization failed:', error);
    }
  }

  // Public API Methods
  public registerScript(script: ThirdPartyScript): void {
    this.scripts.set(script.name, script);
    this.log(`📝 [ThirdPartyLoader] Registered script: ${script.name} (priority: ${script.priority})`);
  }

  public isScriptLoaded(name: string): boolean {
    return this.loadedScripts.has(name);
  }

  public isScriptLoading(name: string): boolean {
    return this.loadingScripts.has(name);
  }

  public getLoadedScripts(): string[] {
    return Array.from(this.loadedScripts);
  }

  public trackEvent(eventName: string, parameters?: Record<string, any>): void {
    if (this.isScriptLoaded('google-analytics') && (window as any).gtag) {
      (window as any).gtag('event', eventName, parameters);
      this.log(`📈 [ThirdPartyLoader] Event tracked: ${eventName}`, parameters);
    } else {
      this.log(`⚠️ [ThirdPartyLoader] Cannot track event (Analytics not ready): ${eventName}`);
    }
  }

  public forceLoadAll(): void {
    this.log('🔧 [ThirdPartyLoader] Force loading all scripts...');
    this.startLoadingScripts();
  }

  // 🎯 NEW: On-demand AdSense loading for performance optimization
  public async loadAdSenseOnDemand(): Promise<boolean> {
    const adSenseScript = this.scripts.get('google-adsense');
    
    if (!adSenseScript) {
      this.log('⚠️ [ThirdPartyLoader] AdSense script not registered');
      return false;
    }

    if (this.loadedScripts.has('google-adsense')) {
      this.log('✅ [ThirdPartyLoader] AdSense already loaded');
      return true;
    }

    if (this.loadingScripts.has('google-adsense')) {
      this.log('⏱️ [ThirdPartyLoader] AdSense already loading, waiting...');
      // Wait for loading to complete
      while (this.loadingScripts.has('google-adsense')) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      return this.loadedScripts.has('google-adsense');
    }

    this.log('🚀 [ThirdPartyLoader] Loading AdSense on-demand...');
    
    try {
      await this.loadScript(adSenseScript);
      this.log('✅ [ThirdPartyLoader] AdSense loaded on-demand successfully');
      return true;
    } catch (error) {
      this.log('❌ [ThirdPartyLoader] AdSense on-demand loading failed:', error);
      return false;
    }
  }

  public getStatus(): {
    initialized: boolean;
    loaded: string[];
    loading: string[];
    registered: string[];
  } {
    return {
      initialized: this.initialized,
      loaded: Array.from(this.loadedScripts),
      loading: Array.from(this.loadingScripts),
      registered: Array.from(this.scripts.keys())
    };
  }

  private log(...args: any[]): void {
    if (this.config.enabled || process.env.NODE_ENV === 'development') {
      // 🔧 Add device type to log messages for easier debugging
      const deviceType = this.isMobile ? '📱' : '💻';
      console.log(deviceType, ...args);
      
      // Store performance logs for debugging
      if (!window.__PERFORMANCE_LOGS__) {
        window.__PERFORMANCE_LOGS__ = [];
      }
      
      window.__PERFORMANCE_LOGS__.push({
        timestamp: Date.now(),
        message: args.join(' '),
        device: this.isMobile ? 'mobile' : 'desktop'
      });
    }
  }
}

// Global singleton instance
let loaderInstance: OptimizedThirdPartyLoader | null = null;

export const getThirdPartyLoader = (): OptimizedThirdPartyLoader => {
  if (!loaderInstance) {
    loaderInstance = new OptimizedThirdPartyLoader();
  }
  return loaderInstance;
};

// Convenience functions
export const trackEvent = (eventName: string, parameters?: Record<string, any>): void => {
  getThirdPartyLoader().trackEvent(eventName, parameters);
};

export const isAnalyticsReady = (): boolean => {
  return getThirdPartyLoader().isScriptLoaded('google-analytics');
};

export const isAdSenseReady = (): boolean => {
  return getThirdPartyLoader().isScriptLoaded('google-adsense');
};

// 🎯 NEW: Load AdSense only when needed for banner display
export const loadAdSenseOnDemand = async (): Promise<boolean> => {
  return await getThirdPartyLoader().loadAdSenseOnDemand();
};

// Type declarations
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
    adsbygoogle: any[];
    __PERFORMANCE_LOGS__?: Array<{
      timestamp: number;
      message: string;
      device: string;
    }>; // 🔧 NEW: Performance debugging logs
  }
} 